// Display formatting. Turkish locale throughout: a comma decimal separator, Turkish month
// names, and `localeCompare(…, 'tr')` wherever names are sorted.

const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const IST = 'Europe/Istanbul';

function partsIst(iso: string) {
  const d = new Date(iso);
  const f = new Intl.DateTimeFormat('tr-TR', {
    timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(d)) if (p.type !== 'literal') out[p.type] = p.value;
  return out;
}

/** '28.08 · 10:14' — the compact form used in tables. */
export function shortDateTime(iso: string): string {
  const p = partsIst(iso);
  return `${p.day}.${p.month} · ${p.hour}:${p.minute}`;
}

/** '28.08.2026 10:14' */
export function fullDateTime(iso: string): string {
  const p = partsIst(iso);
  return `${p.day}.${p.month}.${p.year} ${p.hour}:${p.minute}`;
}

/** '28.08.2026' */
export function shortDate(iso: string): string {
  const p = partsIst(iso);
  return `${p.day}.${p.month}.${p.year}`;
}

/** '28 Ağustos 2026' */
export function longDate(iso: string): string {
  const p = partsIst(iso);
  return `${Number(p.day)} ${MONTHS_TR[Number(p.month) - 1]} ${p.year}`;
}

/** Score out of 10, Turkish decimal comma: 8.6 → '8,6'. null → '—'. */
export function score10(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(1).replace('.', ',');
}

/** A criterion average is also /10. */
export const avg10 = score10;

/** Signed delta between two averages: +0,4 / −1,2 / '' when either side is missing. */
export function delta10(now: number | null, prev: number | null): string {
  if (now === null || prev === null) return '';
  const d = now - prev;
  if (Math.abs(d) < 0.05) return '±0,0';
  return `${d > 0 ? '▲ ' : '▼ '}${Math.abs(d).toFixed(1).replace('.', ',')}`;
}

export function fileSize(bytes: number): string {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1).replace('.', ',')} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

/** 'Deniz Arslan' → 'DA' */
export function initials(name: string): string {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toLocaleUpperCase('tr-TR');
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase('tr-TR');
}

/**
 * Colour tone for a /10 value — the mockup's four bands: ≥9 green, ≥8 accent, ≥7 amber,
 * below red. A critical call is always red, a missing value neutral. Display only: the
 * quality LABEL ('Çok İyi' / 'İyi' / 'Geliştirilmeli') still comes from the server.
 */
export type Tone = 'good' | 'accent' | 'warn' | 'bad' | 'neu';

export function scoreTone(score: number | null | undefined, hasCritical = false): Tone {
  if (hasCritical) return 'bad';
  if (score === null || score === undefined) return 'neu';
  if (score >= 9) return 'good';
  if (score >= 8) return 'accent';
  if (score >= 7) return 'warn';
  return 'bad';
}

export function toneVar(tone: Tone): string {
  switch (tone) {
    case 'good': return 'var(--good)';
    case 'accent': return 'var(--accent)';
    case 'warn': return 'var(--warn)';
    case 'bad': return 'var(--bad)';
    default: return 'var(--faint)';
  }
}

export const STATUS_LABELS: Record<string, string> = {
  published: 'Yayında',
  objected: 'İtiraz edildi',
  revised: 'Puan güncellendi',
  upheld: 'İtiraz reddedildi',
};

export const STATUS_TONE: Record<string, Tone> = {
  published: 'neu',
  objected: 'warn',
  revised: 'good',
  upheld: 'neu',
};

export const ROLE_TONE: Record<string, Tone> = {
  agent: 'accent',
  qms: 'good',
  supervisor: 'warn',
  admin: 'bad',
};

/** The sidebar kicker under the product name — the mockup's "QMS" / "AGENT". */
export const ROLE_KICKER: Record<string, string> = {
  agent: 'AGENT',
  qms: 'QMS',
  supervisor: 'SÜPERVİZÖR',
  admin: 'YÖNETİCİ',
};

/** 'YYYY-MM-DDTHH:MM' for a datetime-local input, defaulted to now in Istanbul. */
export function nowLocalInput(): string {
  return toLocalInput(new Date().toISOString());
}

/**
 * A stored instant → the 'YYYY-MM-DDTHH:MM' a datetime-local input expects, in ISTANBUL
 * wall-clock. The browser's own `toISOString().slice(0,16)` would render the viewer's local
 * time, so an editor abroad would be shown (and would then re-save) a shifted call time —
 * the server reads a bare value back as Istanbul (parseCallAt), so both ends must agree.
 */
export function toLocalInput(iso: string): string {
  const p = partsIst(iso);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
